// helpers/pdfRemitocs.js
import { jsPDF } from "jspdf";
import { getRemitocById } from "./rutaRemitocs";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("es-AR") : "-");
const fmtTime = (t) =>
  t ? String(t).slice(0, 5) : "-"; // espera "HH:mm" y recorta por las dudas
const fmtNum = (n, dec = 2) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });

// 🔒 String seguro para jsPDF.text()
const S = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (v instanceof Date) return v.toLocaleDateString("es-AR");
  if (typeof v === "number") return v.toLocaleString("es-AR");
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (v.razonsocial) return String(v.razonsocial);
  if (v.transporte) return String(v.transporte);
  if (v.empresa) return String(v.empresa);
  if (v.nombre) return String(v.nombre);
  if (v.localidad) return String(v.localidad);
  if (v.provincia) return String(v.provincia);
  return String(v);
};

/** Carga imagen como dataURL; si falla, devuelve null. */
async function loadImageAsDataURL(url) {
  if (!url) return null;
  try {
    const resp = await fetch(url, { mode: "cors" });
    if (!resp.ok) return null;
    const ct = resp.headers.get("content-type") || "";
    if (!ct.startsWith("image/")) return null;

    const blob = await resp.blob();
    const dataURL = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
    return dataURL;
  } catch {
    return null;
  }
}

// ───────────────── HEADER ─────────────────
function drawHeader(doc, y, logoDataURL, empresa) {
  const pageW = doc.internal.pageSize.getWidth();

  if (logoDataURL) {
    try {
      doc.addImage(logoDataURL, undefined, 12, y, 38, 18);
    } catch {}
  }

  const titulo = S(
    empresa?.nombre ?? empresa?.razonsocial ?? "SAN PANCRACIO SRL"
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(titulo, pageW / 2, y + 7, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  const lineaDir = S(empresa?.direccion);
  const lineaContacto = [S(empresa?.telefono), S(empresa?.email)]
    .filter(Boolean)
    .join("   •   ");

  let yy = y + 13;
  if (lineaDir) {
    doc.text(lineaDir, pageW / 2, yy, { align: "center" });
    yy += 4;
  }
  if (lineaContacto) {
    doc.text(lineaContacto, pageW / 2, yy, { align: "center" });
  }

  doc.setDrawColor(50);
  doc.line(12, y + 22, pageW - 12, y + 22);
}

// ───────────────── UTILIDADES ─────────────────
function drawPair(doc, x, y, label, value, w = 65, h = 12, multiline = false) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(S(label), x, y);

  doc.setFont("helvetica", "normal");

  if (multiline) {
    // max 2 líneas para que no se desborde
    const txt = S(value ?? "-");
    let lines = doc.splitTextToSize(txt, w - 4);
    if (lines.length > 2) lines = lines.slice(0, 2);
    doc.text(lines, x, y + 5);
  } else {
    doc.text(S(value ?? "-"), x, y + 5);
  }

  doc.setDrawColor(200);
  doc.rect(x - 2, y - 4, w, h);
}

function drawSectionTitle(doc, y, text) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setDrawColor(120);
  doc.setFillColor(235, 235, 235);
  doc.rect(12, y - 6, pageW - 24, 10, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(S(text), 16, y + 1);
}

// ───────────────── FUNCIÓN PRINCIPAL ─────────────────
export async function generarPDFRemitoc(remitocId, opts = {}) {
  const resp = await getRemitocById(remitocId);

  if (resp?.ok === false) {
    alert(resp?.error || "No se pudo obtener el remito C.");
    return;
  }

  const r = resp?.remitoc || resp?.remito || resp || {};
  if (!r || Object.keys(r).length === 0) {
    alert("No se encontró el remito C.");
    return;
  }

  const logoDataURL = await loadImageAsDataURL(opts.logoUrl);

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = 12;

  drawHeader(doc, y, logoDataURL, opts.empresa || {});
  y += 30;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(`REMITO CAMPO N° ${S(r.codrem) || "-"}`, 12, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  // doc.text(`Fecha: ${fmtDate(r.fecha)}`, 140, y);
  // 👉 Fecha + Hora en NEGRITA
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  const fechaHoraTxt = `Fecha: ${fmtDate(r.fecha)}   Hora: ${fmtTime(r.horaCarga)}`;
  doc.text(fechaHoraTxt, 200, y, { align: "right" }); // alineado a la derecha
  y += 8;

  // ── Datos principales ──
  drawSectionTitle(doc, y, "Datos");
  y += 12;

  // Fila 1: Proveedor / Cliente / Transporte (multilínea + más ancho transporte)
  // Total ancho útil ≈ 186mm -> 14 + 70 + 70 + 46 = 200, entra justo
  drawPair(
    doc,
    14,
    y,
    "Proveedor",
    r?.proveedor?.razonsocial ?? r?.proveedor ?? "-",
    70,
    24,
    true
  );
  drawPair(
    doc,
    84,
    y,
    "Cliente",
    r?.cliente?.razonsocial ?? r?.cliente ?? "-",
    70,
    24,
    true
  );
  drawPair(
    doc,
    154,
    y,
    "Transporte",
    r?.transporte?.transporte ?? r?.transporte ?? "-",
    46,
    24,
    false
  );
  y += 28; // un poco más por la altura grande

  // Fila 2: Chofer / Lote / Variedad
  drawPair(
    doc,
    14,
    y,
    "Chofer",
    r?.chofer?.nombre ?? r?.chofer ?? "-"
  );
  drawPair(doc, 84, y, "Lote", r?.lote?.lote ?? r?.lote ?? "-");
  drawPair(doc, 154, y, "Variedad", r.variedad || "-");
  y += 18;

  // Estado / observaciones
  if (r.estado || r.observa || r.remitoint) {
    drawSectionTitle(doc, y, "Estado / Observaciones");
    y += 12;

    drawPair(
      doc,
      14,
      y,
      "Estado",
      r?.estado?.estado ?? r?.estado ?? "-"
    );
    drawPair(
      doc,
      84,
      y,
      "Observación",
      r?.observa?.observa ?? r?.observa ?? "-"
    );
    drawPair(doc, 154, y, "Remito Interno", r.remitoint || "-");
    y += 18;
  }

  // ── Detalle de cargas ──
  drawSectionTitle(doc, y, "Detalle de Cargas");
  y += 12;

  const startX = 14;
  const colW = 46;
  const rowH = 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  const headers = ["KG Campo", "KG Recibidos", "Diferencia", "Variedad"];
  headers.forEach((h, i) => {
    const x = startX + i * colW;
    doc.rect(x, y, colW, rowH);
    doc.text(S(h), x + 1.5, y + 6);
  });

  doc.setFont("helvetica", "normal");
  const diferencia =
    Number(r.kgCampo || 0) - Number(r.kgRecibido || 0);

  const vals = [
    fmtNum(r.kgCampo, 0),
    fmtNum(r.kgRecibido, 0),
    fmtNum(diferencia, 0),
    S(r.variedad || "-"),
  ];
  const y2 = y + rowH;
  vals.forEach((v, i) => {
    const x = startX + i * colW;
    doc.rect(x, y2, colW, rowH);
    doc.text(S(v), x + 1.5, y2 + 6);
  });

  y += rowH * 2 + 6;

  // ── Totales y referencias ──
  drawSectionTitle(doc, y, "Totales y Referencias");
  y += 12;

  const colWRef = 90;

  drawPair(doc, 14, y, "Nro DTV Compra", r.nroDTVCompra || "-", colWRef);
  drawPair(doc, 114, y, "Nro DTV Venta", r.nroDTVVenta || "-", colWRef);
  y += 18;

  drawPair(doc, 14, y, "Factura Compra", r.factcompra || "-", colWRef);
  drawPair(doc, 114, y, "Factura Venta", r.factventa || "-", colWRef);
  y += 18;

  // ── Notas ──
  drawSectionTitle(doc, y, "Notas");
  y += 12;
  doc.setDrawColor(200);
  doc.rect(12, y - 6, 186, 24);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const nota = S(r.nota);
  const wrapped = doc.splitTextToSize(nota, 182);
  doc.text(wrapped, 14, y);
  y += 28;

  // Pie
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.text(
    "Documento electrónico generado por el sistema San Pancracio",
    12,
    292
  );

  const filename = `RemitoCampo_${S(r.codrem) || remitocId}.pdf`;
  doc.save(filename);
}
